import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Package, Bell, BellOff, Volume2, VolumeX, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

  // Notification sound: uploaded by user, served from /public/sounds/ — used
  // ONLY for new_order events so vendors hear the "ka-ching" they associate
  // with revenue. Pickup / shipment / dispute events use the synthesized
  // tones below so they're audibly distinct from a new sale.
const ORDER_SOUND_URL = '/sounds/new-order.mp3';

/**
 * Notification Sound Matrix — Web Audio API based
 * Generates different tones based on notification severity:
 *   - critical: Double ascending chime (new high-value order, urgent alert)
 *   - order:    Ka-Ching cash register sound (new order)
 *   - warning:  Two-tone alert (sync failure, payment issue)
 *   - info:     Soft single ping (status update, general info)
 */
const NotificationSounds = {
  _ctx: null,
  _getContext() {
    if (!this._ctx || this._ctx.state === 'closed') {
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this._ctx.state === 'suspended') {
      this._ctx.resume();
    }
    return this._ctx;
  },
  
  _playTone(frequency, duration, type = 'sine', volume = 0.3, delay = 0) {
    try {
      const ctx = this._getContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(volume, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + duration);
    } catch (e) { /* AudioContext not available */ }
  },
  
  critical() {
    // Double ascending chime — C5 → E5 → G5
    this._playTone(523, 0.15, 'sine', 0.35, 0);
    this._playTone(659, 0.15, 'sine', 0.35, 0.15);
    this._playTone(784, 0.25, 'sine', 0.4, 0.3);
    this._playTone(1047, 0.3, 'sine', 0.3, 0.5);
  },
  
  order() {
    // Ka-Ching — bright metallic two-note (simulates cash register)
    this._playTone(1200, 0.08, 'square', 0.15, 0);
    this._playTone(1600, 0.12, 'sine', 0.25, 0.05);
    this._playTone(2400, 0.2, 'sine', 0.2, 0.12);
  },

  pickup() {
    // Pickup confirmed — gentle two-note "delivery truck horn" (mid-range
    // descending pair, distinct from Ka-Ching). Sounds reassuring, not urgent.
    this._playTone(587, 0.18, 'triangle', 0.22, 0);   // D5
    this._playTone(440, 0.28, 'triangle', 0.18, 0.18); // A4
  },

  shipment() {
    // Shipment status update — short single chirp
    this._playTone(740, 0.12, 'sine', 0.18, 0);  // F#5
    this._playTone(988, 0.16, 'sine', 0.15, 0.1); // B5
  },
  
  warning() {
    // Two-tone alert — descending
    this._playTone(880, 0.2, 'triangle', 0.3, 0);
    this._playTone(660, 0.3, 'triangle', 0.25, 0.25);
  },
  
  info() {
    // Soft single ping
    this._playTone(880, 0.15, 'sine', 0.15, 0);
  }
};

/**
 * Order Notification Provider
 * 
 * Notifications are triggered by Shopify webhooks (orders/create).
 * Frontend fetches from /api/dashboard/vendor/notifications endpoint.
 * No constant polling - just periodic checks to see if webhooks delivered new orders.
 */
const OrderNotificationProvider = () => {
  const { user, apiCall } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const savedPref = localStorage.getItem('orderNotificationSound');
    return savedPref !== null ? savedPref === 'true' : true;
  });
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const audioRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const lastNotificationIdRef = useRef(null);

  // Persist last-seen notification id per-vendor so vendors who were offline
  // when new orders landed still get the "ka-ching" chime on their next
  // dashboard load (instead of silence because the socket/poll event was
  // already consumed).
  const lastSeenKey = user?.id ? `vendor_last_seen_notif_${user.id}` : 'vendor_last_seen_notif';
  const initializedLastSeenRef = useRef(false);
  
  // Initialize audio (fallback for browsers that block Web Audio API)
  useEffect(() => {
    audioRef.current = new Audio(ORDER_SOUND_URL);
    audioRef.current.volume = 0.6;
    audioRef.current.preload = 'auto';
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  
  // Play notification sound — picks a distinct tone per event type so vendors
  // can tell apart a new order ("ka-ching" MP3) from pickup/shipment updates
  // (synthesized chirps).
  //   • new_order (default) → uploaded MP3 with Web Audio fallback
  //   • pickup_*            → mellow two-note descending pair
  //   • shipment_update     → short ascending chirp
  //   • critical            → ascending alarm chime
  //   • info / *            → soft single ping
  const playNotificationSound = useCallback((notifType = 'new_order', severity = 'order') => {
    if (!soundEnabled) return;
    const t = String(notifType || '').toLowerCase();
    let toneKey;
    if (t.includes('pickup')) toneKey = 'pickup';
    else if (t.includes('shipment') || t.includes('delivery') || t.includes('rto') || t.includes('ndr')) toneKey = 'shipment';
    else if (severity === 'critical') toneKey = 'critical';
    else if (t === 'new_order' || t === 'order' || t === '') toneKey = 'order';
    else toneKey = 'info';

    // Use uploaded ka-ching MP3 ONLY for actual new orders. Everything else
    // uses the Web Audio synthesized tones — so pickup/shipment events sound
    // distinct from new revenue.
    if (toneKey === 'order' && audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {
          try { NotificationSounds.order(); } catch (e) { /* noop */ }
        });
        return;
      } catch (e) { /* fall through */ }
    }
    try {
      if (window.AudioContext || window.webkitAudioContext) {
        NotificationSounds[toneKey]?.() || NotificationSounds.info();
      }
    } catch (e) { /* noop */ }
  }, [soundEnabled]);
  
  // Use ref for previous notifications to avoid dependency cycle
  const prevNotificationsRef = useRef([]);
  const authFailureCountRef = useRef(0);
  
  // Fetch notifications from webhook-triggered endpoint
  const fetchNotifications = useCallback(async () => {
    if (!user || !['vendor', 'admin', 'super_admin'].includes(user.role)) return;

    // If we hit even ONE silent auth failure, stop polling immediately.
    // The previous "tolerate 3 failures" approach generated the
    // "[Auth] 401 attempting refresh" console-spam the user reported,
    // because each poll cycle attempted (and lost) the refresh race.
    // The next foreground action will reauthenticate; until then the
    // bell stays quiet.
    if (authFailureCountRef.current >= 1) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Admins use the admin notifications endpoint; vendors use their own
    const endpoint = user.role === 'vendor'
      ? '/api/dashboard/vendor/notifications'
      : '/api/dashboard/admin/notifications';

    try {
      // silentAuth: a transient 401 on this background poller must NOT
      // trigger the global logout+redirect. Polling gracefully stops
      // locally (authFailureCountRef >= 3 below) instead of kicking the
      // admin out of the page they're working on. The symptom we fixed
      // was an "Admin page keeps refreshing" loop when the session
      // refresh failed on this exact endpoint.
      const response = await apiCall(endpoint, { silentAuth: true });
      if (response === null) {
        // silent auth failure — increment counter and stop polling
        authFailureCountRef.current += 1;
        return;
      }
      authFailureCountRef.current = 0;  // reset on any successful call
      if (response?.notifications) {
        const newNotifications = response.notifications;

        // Seed lastNotificationIdRef from persisted "last seen" on the first
        // successful fetch. If a new order arrived while the vendor was
        // offline, latestId !== persisted id → chime fires below.
        if (!initializedLastSeenRef.current) {
          try {
            const persisted = localStorage.getItem(lastSeenKey);
            if (persisted) lastNotificationIdRef.current = persisted;
          } catch (e) { /* localStorage unavailable */ }
          initializedLastSeenRef.current = true;
        }

        // Check if there are truly NEW notifications (not seen before)
        if (newNotifications.length > 0) {
          const latestId = newNotifications[0]?.id;

          // If we have a new notification we haven't processed
          if (latestId && latestId !== lastNotificationIdRef.current) {
            // brand-new = anything the user hasn't already acknowledged.
            // On a fresh page load (prev list empty) this includes every
            // notification newer than the persisted lastSeen id, so the
            // offline-missed order case chimes exactly once.
            const prevIds = prevNotificationsRef.current.map(n => n.id);
            const lastSeenId = lastNotificationIdRef.current;
            const brandNew = newNotifications.filter(n => {
              if (prevIds.includes(n.id)) return false;
              if (lastSeenId && n.id === lastSeenId) return false;
              return true;
            });

            // Chime when:
            //  - live poll delivered a new order (prev list was populated), OR
            //  - page just (re)loaded and the latest id differs from the
            //    persisted last-seen id (offline-missed orders).
            const shouldChime = brandNew.length > 0 && (
              lastSeenId !== null || prevIds.length > 0
            );

            if (shouldChime) {
              // Determine severity based on order value + pick the most
              // representative type from the new batch (so a fresh new_order
              // wins over a stale shipment update if both arrived together).
              const maxAmount = Math.max(...brandNew.map(n => n.total_amount || 0));
              const severity = maxAmount >= 5000 ? 'critical' : 'order';
              const orderType = brandNew.find(n => String(n.type || '').includes('order'))?.type
                || brandNew[0]?.type
                || 'new_order';
              playNotificationSound(orderType, severity);
              
              // Show toast for each new order (max 3)
              brandNew.slice(0, 3).forEach((notif, index) => {
                setTimeout(() => {
                  toast.success(
                    <div className="flex items-center gap-3">
                      <Package className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-semibold">New Order! {notif.order_number}</p>
                        <p className="text-sm text-gray-600">
                          {notif.customer_name} - ₹{notif.total_amount?.toLocaleString('en-IN') || '0'}
                        </p>
                      </div>
                    </div>,
                    {
                      duration: 8000,
                      position: 'top-right',
                    }
                  );
                }, index * 600);
              });
              
              if (brandNew.length > 3) {
                setTimeout(() => {
                  toast.info(`+${brandNew.length - 3} more new orders`, {
                    duration: 5000,
                    position: 'top-right',
                  });
                }, 2000);
              }
            }
            
            lastNotificationIdRef.current = latestId;
            // Persist so a reload doesn't replay the chime for this batch
            try {
              if (latestId) localStorage.setItem(lastSeenKey, latestId);
            } catch (e) { /* ignore */ }
          }
          
          prevNotificationsRef.current = newNotifications;
          setNotifications(newNotifications);
          setUnseenCount(response.unseen_count || newNotifications.length);
        } else {
          prevNotificationsRef.current = [];
          setNotifications([]);
          setUnseenCount(0);
        }
      }
    } catch (error) {
      const msg = String(error?.message || error || '');
      // Session expired or auth failure — stop polling to avoid console spam
      if (msg.includes('Session expired') || msg.includes('401') || msg.includes('unauthor')) {
        authFailureCountRef.current += 1;
      }
      // Only log first 2 failures to avoid spamming the console
      if (authFailureCountRef.current <= 2) {
        console.error('Failed to fetch notifications:', error);
      }
    }
  }, [user, apiCall, playNotificationSound]);
  
  // Set up periodic check for webhook-delivered notifications
  // Less aggressive than polling - just checking if webhooks delivered anything.
  // iter-251 perf — Skip when tab is backgrounded; refetch immediately
  // when the tab returns to foreground so the bell catches up.
  useEffect(() => {
    if (['vendor', 'admin', 'super_admin'].includes(user?.role)) {
      const tick = () => {
        if (!document.hidden) fetchNotifications();
      };
      const initialTimer = setTimeout(() => { fetchNotifications(); }, 2000);
      checkIntervalRef.current = setInterval(tick, 60000);
      document.addEventListener('visibilitychange', tick);
      return () => {
        clearTimeout(initialTimer);
        if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
        document.removeEventListener('visibilitychange', tick);
      };
    }
  }, [user?.role]); // fetchNotifications intentionally excluded — uses refs
  
  // Toggle sound
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('orderNotificationSound', newValue.toString());
    
    if (newValue) {
      playNotificationSound('order');
      toast.success('Order notification sounds enabled');
    } else {
      toast.info('Order notification sounds muted');
    }
  };
  
  // Mark all as seen
  const markAllSeen = async () => {
    try {
      await apiCall('/api/dashboard/vendor/notifications/mark-seen', { 
        method: 'POST',
        body: JSON.stringify({ mark_all: true })
      });
      // Remember the newest id so reloads don't re-chime for these
      try {
        const latestId = notifications[0]?.id;
        if (latestId) localStorage.setItem(lastSeenKey, latestId);
      } catch (e) { /* ignore */ }
      setNotifications([]);
      setUnseenCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to mark notifications as seen');
    }
  };

  // When the vendor opens the floating panel, treat the latest id as
  // acknowledged locally so a page reload won't chime for the same batch.
  const handleOpenPanel = () => {
    const nextOpen = !showNotificationPanel;
    setShowNotificationPanel(nextOpen);
    if (nextOpen) {
      try {
        const latestId = notifications[0]?.id;
        if (latestId) {
          localStorage.setItem(lastSeenKey, latestId);
          lastNotificationIdRef.current = latestId;
        }
      } catch (e) { /* ignore */ }
    }
  };
  
  // Only show for vendors
  if (!user || user.role !== 'vendor') {
    return null;
  }
  
  return (
    <>
      {/* Floating notification bell (only for vendors on desktop/tablet) */}
      {/* Hidden on mobile as notification badge is in bottom nav's Orders tab */}
      <div className="hidden md:flex fixed bottom-6 right-6 z-50 flex-col items-end gap-2">
        {/* Notification panel */}
        {showNotificationPanel && (
          <div className="bg-white rounded-lg shadow-xl border p-4 w-80 mb-2 animate-in slide-in-from-bottom-2">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">
                New Orders {unseenCount > 0 && <span className="text-green-600">({unseenCount})</span>}
              </h4>
              <Button variant="ghost" size="sm" onClick={() => setShowNotificationPanel(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {notifications.length > 0 ? (
              <>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {notifications.map(notif => (
                    <div key={notif.id} className="p-2 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-green-800">{notif.order_number}</span>
                        <span className="text-green-700 font-semibold">₹{notif.total_amount?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      <p className="text-xs text-green-600">{notif.customer_name}</p>
                      <p className="text-[10px] text-gray-500 mt-1">
                        {notif.item_count} item{notif.item_count !== 1 ? 's' : ''} • {notif.first_item}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t">
                  <Button size="sm" variant="outline" className="flex-1" onClick={markAllSeen}>
                    Clear All
                  </Button>
                  <Button size="sm" className="flex-1" onClick={() => window.location.href = '/vendor/orders'}>
                    View Orders
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No new orders</p>
                <p className="text-xs mt-1">Orders from Shopify will appear here</p>
              </div>
            )}
          </div>
        )}
        
        {/* Sound toggle button */}
        <Button
          size="icon"
          variant="outline"
          className="rounded-full shadow-lg bg-white"
          onClick={toggleSound}
          title={soundEnabled ? 'Mute notifications' : 'Enable notification sounds'}
        >
          {soundEnabled ? (
            <Volume2 className="h-4 w-4 text-green-600" />
          ) : (
            <VolumeX className="h-4 w-4 text-gray-400" />
          )}
        </Button>
        
        {/* Notification bell */}
        <Button
          size="icon"
          className={`rounded-full shadow-lg ${unseenCount > 0 ? 'bg-green-600 hover:bg-green-700 animate-pulse' : 'bg-gray-600 hover:bg-gray-700'}`}
          onClick={() => handleOpenPanel()}
        >
          {unseenCount > 0 ? (
            <Bell className="h-5 w-5 text-white" />
          ) : (
            <BellOff className="h-5 w-5 text-white" />
          )}
          {unseenCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
              {unseenCount > 9 ? '9+' : unseenCount}
            </Badge>
          )}
        </Button>
      </div>
    </>
  );
};

export default OrderNotificationProvider;
